import {
  Config,
  EmoRacha,
  EmoRachaActive,
  HeartFilled,
  Pack,
  Progress,
  User as UserIcon,
} from "@assets"
import { Link, router } from "@inertiajs/react"
import { LayoutDashboard, LogOut, MessageSquare, Shield } from "lucide-react"

import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { UserInfo } from "@/components/user-info"
import { useMobileNavigation } from "@/hooks/use-mobile-navigation"
import {
  businessOrdersPath,
  favoritesPath,
  ordersPath,
  sessionPath,
  settingsProfilePath,
  userPath,
} from "@/routes"
import type { User } from "@/types"

interface UserMenuContentProps {
  auth: {
    session: {
      id: string
    }
    user: User
  }
}

export function UserMenuContent({ auth }: UserMenuContentProps) {
  const { session, user } = auth
  const cleanup = useMobileNavigation()
  const profileCompletion = Math.max(
    0,
    Math.min(100, user.profile_completion_percentage ?? 0),
  )
  const streakDays = user.streak_count ?? 0
  const favoritesCount = user.favorites_count ?? 0

  const handleLogout = () => {
    cleanup()
    router.flushAll()
  }

  return (
    <>
      <DropdownMenuLabel className="p-0 font-normal">
        <UserInfo user={user} showNeighborhood={true} />
      </DropdownMenuLabel>

      <div className="px-2">
        {/* Streak Status - mismo estilo que mobile */}
        <div className="flex items-center gap-2">
          <img
            src={streakDays > 0 ? EmoRachaActive : EmoRacha}
            className="-ml-1 h-[33px] w-auto"
            alt=""
          />
          <span className="text-muted-foreground text-[13px] tracking-[0.2px]">
            {streakDays > 0 ? (
              <>
                Racha actual{" "}
                <span className="text-secondary text-sm font-semibold">
                  {streakDays}
                </span>
              </>
            ) : (
              "Sin racha actual"
            )}
          </span>
        </div>

        {/* Profile Progress - mismo estilo que mobile */}
        <Link href={settingsProfilePath()} prefetch className="block">
          <div className="flex items-center gap-2 py-2 text-[13px] tracking-[0.2px]">
            <Progress
              className={`h-[24px] w-auto ${profileCompletion === 100 ? "fill-active" : "fill-star"}`}
            />
            <span className="text-muted-foreground">
              Progreso de perfil{" "}
              <span
                className={`font-semibold ${profileCompletion === 100 ? "text-active" : "text-star"}`}
              >
                {profileCompletion}%
              </span>
            </span>
          </div>
          <div className="bg-modal h-[10px] w-full overflow-hidden rounded-full">
            <div
              className={`h-full rounded-full transition-all ${profileCompletion === 100 ? "bg-active" : "bg-star"}`}
              style={{ width: `${profileCompletion}%` }}
            />
          </div>
        </Link>
      </div>

      <DropdownMenuSeparator className="bg-chart-4 mt-4 mb-2" />

      <DropdownMenuGroup>
        <DropdownMenuItem asChild className="px-0">
          <Link
            href={userPath(user.id)}
            prefetch
            className="group flex w-full items-center gap-2"
          >
            <UserIcon className="fill-primary -ml-0.5 size-6 group-hover:fill-white" />
            <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px] group-hover:text-white">
              Ver perfil
            </span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="px-0">
          <Link
            className="group flex w-full items-center gap-2"
            href={favoritesPath()}
            as="button"
            prefetch
            onClick={cleanup}
          >
            <HeartFilled className="text-destructive ml-0.5 size-5" />
            <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px] group-hover:text-white">
              Guardados
              {favoritesCount > 0 && (
                <span className="text-muted-foreground ml-1 group-hover:text-white/70">
                  ({favoritesCount})
                </span>
              )}
            </span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild className="px-0">
          <Link
            className="group flex w-full items-center gap-2"
            href={ordersPath()}
            as="button"
            prefetch
            onClick={cleanup}
          >
            <Pack className="text-primary ml-0.5 size-5 group-hover:text-white" />
            <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px] group-hover:text-white">
              Mis Pedidos
            </span>
          </Link>
        </DropdownMenuItem>
        {user.has_business && (
          <DropdownMenuItem asChild className="px-0">
            <Link
              className="group flex w-full items-center gap-2"
              href={businessOrdersPath()}
              as="button"
              prefetch
              onClick={cleanup}
            >
              <Pack className="text-primary ml-0.5 size-5 group-hover:text-white" />
              <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px] group-hover:text-white">
                Pedidos de mi negocio
              </span>
            </Link>
          </DropdownMenuItem>
        )}
        {user.is_admin && (
          <>
            <DropdownMenuSeparator className="bg-chart-4" />
            <DropdownMenuItem asChild className="px-0">
              <a
                className="group flex w-full items-center gap-2"
                href="/admin"
                onClick={cleanup}
              >
                <LayoutDashboard className="text-primary ml-0.5 size-5 group-hover:text-white" />
                <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px] group-hover:text-white">
                  Panel Admin
                </span>
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="px-0">
              <a
                className="group flex w-full items-center gap-2"
                href="/madmin"
                onClick={cleanup}
              >
                <Shield className="text-primary ml-0.5 size-5 group-hover:text-white" />
                <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px] group-hover:text-white">
                  Moderación (Madmin)
                </span>
              </a>
            </DropdownMenuItem>
          </>
        )}
        {user.role === "moderator" && (
          <>
            <DropdownMenuSeparator className="bg-chart-4" />
            <DropdownMenuItem disabled className="px-0">
              <MessageSquare className="text-primary ml-0.5 size-5" />
              <span className="text-chart-3 ml-2 text-[12px] font-medium tracking-[0.18px]">
                Moderador de Foro
              </span>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem asChild className="px-0">
          <Link
            className="group flex w-full items-center gap-2"
            href={settingsProfilePath()}
            as="button"
            prefetch
            onClick={cleanup}
          >
            <Config className="fill-primary size-5.5 group-hover:fill-white" />
            <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px] group-hover:text-white">
              Configuración
            </span>
          </Link>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator className="bg-chart-4" />
      <DropdownMenuItem asChild className="px-0">
        <Link
          className="group flex w-full items-center gap-2"
          method="delete"
          href={sessionPath(session.id)}
          as="button"
          onClick={handleLogout}
        >
          <LogOut className="text-primary ml-0.5 size-5 group-hover:text-white" />
          <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px] group-hover:text-white">
            Cerrar sesión
          </span>
        </Link>
      </DropdownMenuItem>
    </>
  )
}
